// Every screen in the Hub spec gets a route (screens.json). Built screens are
// mapped below; the rest render a ScreenPlaceholder from the spec, so the whole
// structure is navigable from the start.
import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, redirect, type AnyRoute } from "@tanstack/react-router";
import { useEffect, type FunctionComponent } from "react";
import type { RouteComponent } from "@tanstack/react-router";
import { session } from "./api/session";
import { Shell } from "./app/Shell";
import { AUTH_SCREEN_IDS, ROUTED_SCREENS, type Screen } from "./app/screens";
import { ScreenPlaceholder } from "./components/ScreenPlaceholder";
import { Empty } from "./components/States";
import { helpForScreen, helpHome } from "./app/help";
import { AcceptInvitation } from "./features/auth/AcceptInvitation";
import { ForgotPassword } from "./features/auth/ForgotPassword";
import { ResetPassword } from "./features/auth/ResetPassword";
import { SignIn } from "./features/auth/SignIn";
import { SignUp } from "./features/auth/SignUp";
import { TwoFactor } from "./features/auth/TwoFactor";
import { VerifyEmail } from "./features/auth/VerifyEmail";
import { HomePage } from "./features/home/HomePage";

// Code-split: only the shell, sign-in and placeholders are in the first bundle
// (web PLATFORM-SPEC §10: ≤ 170 KB JS gzip on the initial route).
const UsersPage = lazyRouteComponent(() => import("./features/admin/UsersPage"), "UsersPage");
const ApplicationsPage = lazyRouteComponent(() => import("./features/dev/ApplicationsPage"), "ApplicationsPage");
const TurnOnTwoFactor = lazyRouteComponent(() => import("./features/account/TurnOnTwoFactor"), "TurnOnTwoFactor");
const SettingsPage = lazyRouteComponent(() => import("./features/settings/SettingsPage"), "SettingsPage");

/** Screens that are built. Everything else in the spec is a placeholder. */
const BUILT: Record<string, RouteComponent> = {
  "AUTH-01": SignIn,
  "AUTH-02": TwoFactor,
  "AUTH-03": SignUp,
  "AUTH-04": VerifyEmail,
  "AUTH-05": ForgotPassword,
  "AUTH-06": ResetPassword,
  "AUTH-07": AcceptInvitation,
  "APP-01": HomePage,
  "ACC-05": TurnOnTwoFactor,
  "SET-01": SettingsPage,
  "USR-01": UsersPage,
  "APL-01": ApplicationsPage,
};

export const BUILT_SCREEN_IDS = Object.keys(BUILT);

const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: () => (
    <div className="page">
      <Empty title="Page not found">
        <p>
          There's nothing at this address. <a href="/">Go home</a> or <a href={helpHome()}>visit the help center</a>.
        </p>
      </Empty>
    </div>
  ),
});

// Sign-in and emailed-link pages, outside the shell.
const authRoutes = ROUTED_SCREENS.filter((s) => AUTH_SCREEN_IDS.has(s.id)).map((s) =>
  createRoute({ getParentRoute: () => rootRoute, path: toPath(s.route!), component: componentFor(s) }),
);

// Everything else, inside the signed-in shell.
const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "shell",
  component: Shell,
  beforeLoad: async ({ location }) => {
    if (!(await session.restore())) throw redirect({ to: "/sign-in", search: { redirect: location.href } });
  },
});
const shellRoutes = ROUTED_SCREENS.filter((s) => !AUTH_SCREEN_IDS.has(s.id)).map((s) =>
  createRoute({ getParentRoute: () => shellRoute, path: toPath(s.route!), component: componentFor(s) }),
);

// Help lives on the specs site's help center. These routes keep old /help links
// (and muscle memory) working by sending them there.
function HelpRedirect({ to }: { to: () => string }) {
  useEffect(() => window.location.replace(to()), [to]);
  return (
    <p className="page">
      Opening the <a href={to()}>help center</a>…
    </p>
  );
}
const toScreenHelp = () => helpForScreen(decodeURIComponent(window.location.pathname.split("/").pop() ?? ""));
const helpRoutes: AnyRoute[] = [
  createRoute({ getParentRoute: () => rootRoute, path: "/help", component: () => <HelpRedirect to={helpHome} /> }),
  createRoute({ getParentRoute: () => rootRoute, path: "/help/s/$screenId", component: () => <HelpRedirect to={toScreenHelp} /> }),
  createRoute({ getParentRoute: () => rootRoute, path: "/help/$", component: () => <HelpRedirect to={helpHome} /> }),
];

const routeTree = rootRoute.addChildren([...authRoutes, shellRoute.addChildren(shellRoutes), ...helpRoutes]);

export const router = createRouter({ routeTree, defaultPreload: "intent", scrollRestoration: true });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

function toPath(route: string): string {
  return route.replace(/:([A-Za-z]+)/g, "$$$1");
}

function componentFor(s: Screen): RouteComponent | FunctionComponent {
  const Built = BUILT[s.id];
  if (Built) return Built;
  const Placeholder = () => <ScreenPlaceholder screen={s} />;
  Placeholder.displayName = `Placeholder(${s.id})`;
  return Placeholder;
}
